<template>
  <div v-if="metrics" class="metrics-grid">
    <MetricBar
      title="Memory"
      :value="`${memUsedMb} / ${metrics.mem_total_mb} MB`"
      :percent="memUsagePct"
      :status="memoryStatus"
      color-class="mem"
    />
    <MetricBar
      title="CPU"
      :value="`${metrics.cpu_usage_pct.toFixed(1)}%`"
      :percent="cpuPct"
      :status="cpuStatus"
      color-class="cpu"
    />
    <MetricBar
      title="Disk"
      :value="`${diskUsedMb} / ${metrics.disk_total_mb} MB`"
      :percent="diskUsagePct"
      :status="diskStatus"
      color-class="disk"
    />
    <MetricBar
      title="Temperature"
      :value="`${metrics.temperature_c.toFixed(1)}°C`"
      :percent="tempPct"
      :status="tempStatus"
      color-class="temp"
    />
  </div>
  <div v-else class="no-data">No recent metrics</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { MetricsData } from '@/helpers/api/metrics';
import MetricBar, { type MetricStatus } from './MetricBar/MetricBar.vue';

const props = defineProps<{ metrics: MetricsData | null }>();

// Thresholds for "warning" and "critical" status. Memory/CPU/disk are
// percentages (0–100); temperature is in degrees Celsius.
const THRESHOLDS = {
  mem:   { warn: 75, bad: 85 },
  cpu:   { warn: 70, bad: 90 },
  disk:  { warn: 70, bad: 90 },
  tempC: { warn: 70, bad: 85 },
} as const;

function clampPct(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function getStatusForPercent(
  value: number,
  thresholds: { warn: number; bad: number }
): MetricStatus {
  const v = clampPct(value);
  if (v < thresholds.warn) return 'good';
  if (v < thresholds.bad) return 'warn';
  return 'bad';
}

const memUsedMb = computed(() =>
  props.metrics
    ? Math.max(props.metrics.mem_total_mb - props.metrics.mem_free_mb, 0)
    : 0
);
const memUsagePct = computed(() =>
  props.metrics && props.metrics.mem_total_mb > 0
    ? (memUsedMb.value / props.metrics.mem_total_mb) * 100
    : 0
);

const diskUsedMb = computed(() =>
  props.metrics
    ? Math.max(props.metrics.disk_total_mb - props.metrics.disk_free_mb, 0)
    : 0
);
const diskUsagePct = computed(() =>
  props.metrics && props.metrics.disk_total_mb > 0
    ? (diskUsedMb.value / props.metrics.disk_total_mb) * 100
    : 0
);

const cpuPct = computed(() => clampPct(props.metrics?.cpu_usage_pct ?? 0));

const memoryStatus = computed(() => getStatusForPercent(memUsagePct.value, THRESHOLDS.mem));
const cpuStatus    = computed(() => getStatusForPercent(cpuPct.value,      THRESHOLDS.cpu));
const diskStatus   = computed(() => getStatusForPercent(diskUsagePct.value, THRESHOLDS.disk));
const tempStatus   = computed<MetricStatus>(() => {
  const tempC = props.metrics?.temperature_c ?? 0;
  if (tempC < THRESHOLDS.tempC.warn) return 'good';
  if (tempC < THRESHOLDS.tempC.bad)  return 'warn';
  return 'bad';
});
const tempPct = computed(() =>
  clampPct(((props.metrics?.temperature_c ?? 0) / THRESHOLDS.tempC.bad) * 100)
);
</script>

<style scoped lang="scss">
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.no-data {
  color: #6b7280;
  font-style: italic;
}
</style>
